import React, { useMemo } from "react";
import RoundVoteBreakdownChart from "../VoteCharts/RoundVoteBreakdownChart";
import { getTeamVotesByRound } from "./teamRoundVotes";

/**
 * Season timeline for a team: total votes its players polled each round,
 * averaged across the enabled datasets. A team plays once per round, so a round
 * tops out at 6 (3-2-1 sweep) and the heat bands scale to that.
 */
const TeamRoundVoteBreakdown = ({ team, gameVoteData, enabledKeys = [], roster = null }) => {
  // enabledKeys and roster are rebuilt on every render, so key the memo on their
  // contents — scanning every dataset is too costly to redo on each hover.
  const enabledKey = enabledKeys.join(',');
  const rosterKey = roster ? roster.map((p) => p?.name).join('|') : '';

  const values = useMemo(
    () => getTeamVotesByRound(team, gameVoteData, enabledKeys, roster),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [team, gameVoteData, enabledKey, rosterKey]
  );

  if (!team || !gameVoteData) return null;

  const totalVotes = values.reduce((sum, v) => sum + (Number(v) || 0), 0);
  const bestRound = values.reduce(
    (best, v, r) => (v > best.value ? { round: r, value: v } : best),
    { round: null, value: 0 }
  );

  return (
    <div className="bg-slate-950/60 rounded-xl border border-slate-800 p-3 sm:p-4">
      <RoundVoteBreakdownChart values={values} scaleMax={6} ariaLabel="Team votes by round" />

      {/* Season figures for the timeline above */}
      <div className="mt-3 pt-3 border-t border-slate-800/70">
        <div className="flex items-center justify-between gap-3 px-1">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">Best Round</span>
            <span className="text-slate-300 font-mono font-bold tabular-nums text-xs">
              {bestRound.round != null && bestRound.value > 0 ? `R${bestRound.round} · ${bestRound.value.toFixed(2)}` : '—'}
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">Total</span>
            <span className="text-emerald-300 font-mono font-bold tabular-nums text-sm">{totalVotes.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamRoundVoteBreakdown;
